import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

export type ToastKind = "success" | "error" | "info"

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  detail?: string
}

interface ToastContextValue {
  toasts: Toast[]
  push: (kind: ToastKind, message: string, detail?: string) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (kind: ToastKind, message: string, detail?: string) => {
      const id = nextId++
      setToasts((current) => {
        // Collapse identical toasts (e.g. the same relay error from several
        // subscriptions) instead of stacking them.
        if (current.some((toast) => toast.kind === kind && toast.message === message)) return current
        return [...current.slice(-3), { id, kind, message, detail }]
      })
      const ttl = kind === "error" ? 9000 : 4500
      window.setTimeout(() => dismiss(id), ttl)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss])
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error("useToast must be used inside a ToastProvider")
  return context
}
